import type { Maybe } from '@adornis/base/utilTypes.js';
import { DialogHeader } from '@adornis/components/client/DialogHeader.js';
import { guardDialog, showDialog } from '@adornis/dialog/dialog-functions.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { StringAspect } from '@adornis/filter/aspect-schemas/StringAspect.js';
import { stateful, useState } from '@adornis/functional-lit/stateful.js';
import { useQuery } from '@adornis/functional-lit/use-query.js';
import { html, nothing } from 'lit';
import { SgSnackbar } from '../../client/components/ui/sg-snackbar.js';
import { sendSuSUpdateLink } from '../../sus-entry-form/school-selection/api/sendSuSUpdateLink.js';
import { UserListAPI } from '../list/api/UserListAPI.js';

const SendSuSUpdateLinkDialog = stateful(
  (props: { childID: string; withDeadline: boolean; resolve: (value: unknown) => void; reject: () => void }) => {
    const [guardianID, setGuardianID] = useState<Maybe<string>>(null);
    const { value: guardians, loading } = useQuery(
      () =>
        UserListAPI.subscribe(
          0,
          20,
          new AdornisFilter({
            aspects: [
              new StringAspect({
                fieldName: 'sorgeberechtigteKinderIDs',
                mode: 'containsExactOR',
                value: props.childID,
              }),
            ],
          }),
        )({ _id: 1, email: 1, profilDaten: { stammdaten: { vorname: 1, nachname: 1 } } }),
      [props.childID],
    );

    const send = async () => {
      if (!guardianID) return;
      await guardDialog(() => sendSuSUpdateLink(props.childID, guardianID, props.withDeadline)(), {
        success: email => {
          props.resolve(null);
          void SgSnackbar.show(`Aktualisierungslink an ${email} gesendet`);
        },
      });
    };

    return html`
      <div class="dialog-container">
        ${DialogHeader({
          title: props.withDeadline ? 'Aktualisierungslink mit Frist senden' : 'Aktualisierungslink senden',
          close: props.reject,
          closeLabel: 'Dialog schließen',
        })}

        <p>
          Die ausgewählte Person erhält einen Link, der sie anmeldet und das Bewerbungsformular mit den gespeicherten
          Angaben öffnet. Das Absenden setzt den Status wieder auf „Bewerbung“.
        </p>
        ${
          props.withDeadline
            ? html`<p>
                Wird das Formular nicht innerhalb von 21 Tagen erneut abgesendet, wird die Bewerbung automatisch
                abgelehnt, sofern sie bis dahin keine Zusage hat.
              </p>`
            : nothing
        }

        <div class="flex flex-col gap-2" role="radiogroup" aria-label="Sorgeberechtigte">
          ${
            loading
              ? html`<span class="loading loading-spinner" aria-label="Sorgeberechtigte werden geladen"></span>`
              : guardians?.length
                ? guardians.map(
                    guardian => html`
                      <button
                        type="button"
                        role="radio"
                        aria-checked=${guardian._id === guardianID}
                        class="btn h-auto justify-start py-2 text-left ${guardian._id === guardianID ? 'btn-primary' : 'btn-outline'}"
                        @click=${() => setGuardianID(guardian._id)}
                      >
                        <span class="flex flex-col">
                          <span>
                            ${guardian.profilDaten?.stammdaten?.vorname} ${guardian.profilDaten?.stammdaten?.nachname}
                          </span>
                          <span class="font-normal">${guardian.email}</span>
                        </span>
                      </button>
                    `,
                  )
                : html`<p>Diesem Kind ist kein Sorgeberechtigter zugeordnet.</p>`
          }
        </div>

        <div class="flex justify-end">
          <button class="btn btn-primary" ?disabled=${!guardianID} @click=${send}>Link senden</button>
        </div>
      </div>
    `;
  },
);

export const openSendSuSUpdateLinkDialog = (childID: string, withDeadline: boolean) =>
  showDialog({
    content: (resolve, reject) => SendSuSUpdateLinkDialog({ childID, withDeadline, resolve, reject }),
    modalBoxClasses: 'max-w-xl',
  }).catch(() => {});
